import { useMemo } from 'react';
import { useGameStore } from '../../core/store';
import { skillBoards } from '../../data/skillBoards';
import './skills-checkpoint-bar.css';

/** Glow as CURRENT once the player is this close to the board (meters). */
const REACH_SLACK = 1.35;

const LABELS: Record<string, string> = {
  ai: 'AI BASICS',
  cloud: 'CLOUD',
  frontend: 'FRONTEND',
  ml: 'ML',
  three: '3D / R3F',
  backend: 'BACKEND',
  database: 'DATABASES',
  architecture: 'SYSTEMS',
};

function CapIcon() {
  return (
    <svg
      className="skills-cp__cap"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M2.5 9.5 12 4l9.5 5.5L12 15 2.5 9.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M6 11.2v4.3c0 .4 2.4 2.5 6 2.5s6-2.1 6-2.5v-4.3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21.5 9.5v5.2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      className="skills-cp__lock"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden
    >
      <rect
        x="2.5"
        y="5.2"
        width="7"
        height="5.3"
        rx="1.1"
        stroke="currentColor"
        strokeWidth="1.1"
      />
      <path
        d="M4 5.2V3.8a2 2 0 0 1 4 0v1.4"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function SkillsCheckpointBar({ compact = false }: { compact?: boolean }) {
  const unlocked = useGameStore((s) => s.unlockedSkills);
  const runnerZ = useGameStore((s) => s.runnerZ);

  const unlockedSet = useMemo(() => new Set(unlocked), [unlocked]);
  const gained = unlocked.length;
  const total = skillBoards.length;
  const complete = gained >= total;

  const currentIdx = useMemo(() => {
    const next = skillBoards.findIndex((b) => !unlockedSet.has(b.id));
    if (next === -1) return -1;
    return next;
  }, [unlockedSet]);

  return (
    <div
      className={`skills-cp${compact ? ' skills-cp--compact' : ''}`}
      role="status"
      aria-label={`Skills path ${gained} of ${total}`}
    >
      <div className="skills-cp__bar">
        <div className="skills-cp__head">
          <div className="skills-cp__title-row">
            <CapIcon />
            <span className="skills-cp__kicker">Skills path</span>
          </div>
          <p className="skills-cp__sub">
            {complete
              ? 'All checkpoints cleared'
              : `${gained}/${total} unlocked · walk the boards`}
          </p>
        </div>

        <div className="skills-cp__track" aria-hidden>
          <div className="skills-cp__rail" />
          {skillBoards.map((board, i) => {
            const on = unlockedSet.has(board.id);
            const atBoard = runnerZ >= board.z - REACH_SLACK;
            const current = !on && i === currentIdx && atBoard;
            const litLink = i > 0 && unlockedSet.has(skillBoards[i - 1].id);
            const label =
              LABELS[board.id] ?? board.title.slice(0, 8).toUpperCase();

            return (
              <div
                key={board.id}
                className={[
                  'skills-cp__step',
                  on ? 'is-on' : '',
                  current ? 'is-current' : '',
                  litLink ? 'is-link-on' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {i > 0 && <i className="skills-cp__link" />}
                <div className="skills-cp__node">
                  <span>{on ? '✓' : i + 1}</span>
                </div>
                <span className="skills-cp__name">{label}</span>
                <span className="skills-cp__state">
                  {on ? (
                    'CLEARED'
                  ) : current ? (
                    'CURRENT'
                  ) : (
                    <>
                      <LockIcon /> LOCKED
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
